/* eslint-disable camelcase */
import {
  BlueBirdTheme, Button, Typography, useSnackbar,
} from "@kitbird/core";
import {
  createContext, PropsWithChildren, useContext, useEffect, useMemo, useRef, useState, useCallback,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  useInfiniteQuery, useMutation, useQuery, useQueryClient,
} from "react-query";
import { FieldErrors, useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { AxiosError } from "axios";
import Status, { StatusEnums } from "../../../components/Status";
import { ReactComponent as EditIcon } from "../../../assets/icons/edit.svg";
import { ReactComponent as TrashIcon } from "../../../assets/icons/trash.svg";
import { ColumnI } from "../../../components/Table/TableProps";
import {
  IGetBusinessTrxParams, IBusinessTrx, IResponseMstBusinessTrxData,
  ICreateMasterBusinessTrxRequest, IEditMasterBusinessTrxRequest, IFilterCompany,
} from "../../../services/businessTrxService/types";
import {
  createMasterBusinessTrx, deleteMasterBusinessTrx, editMasterBusinessTrx,
  getMasterBusinessTrxFilterAllArea, getMasterBusinessTrxFilterCompanySegment,
  getMasterBusinessTrxFilterDepartment,
  getMasterBusinessTrxFilterPool, getMasterBusinessTrxList,
} from "../../../services/businessTrxService/businessTrxService";
import {
  IBusinessTrxFormValues, IContextValue, ModalType, StepConfigType, TrxType,
} from "./types";
import SvgDiv from "../../../components/SvgDiv";
import { CircleCheck } from "../../../assets/icons";
import {
  businessTrxDefaultFormValue, businessTrxSchema, formatPayload, formatPayloadEdit,
  stepsConfig,
} from "./utils";
import { IOptionItem } from "../../../typings/common";
import useAuth from "../../../components/Authentication/useAuth";

export const getIconProps = (stepConfig: StepConfigType, isEditMode?: boolean) => {
  const { Icon } = stepConfig;
  return {
    iconPassed: <SvgDiv $color={BlueBirdTheme.colors.success[500]}><CircleCheck /></SvgDiv>,
    iconActive: <SvgDiv $color={BlueBirdTheme.colors.primary[500]}><Icon /></SvgDiv>,
    iconInactive: (
      <SvgDiv
        $color={isEditMode ? BlueBirdTheme.colors.success[500] : BlueBirdTheme.colors.gray.disable}
      >
        {isEditMode ? <CircleCheck /> : <Icon />}
      </SvgDiv>
    ),
  };
};

export const Context = createContext<IContextValue | undefined>(undefined);

export const useBusinessTrx = () => {
  const context = useContext(Context);
  if (!context) throw new Error("useBusinessTrx must be used within a BusinessTrx component");
  return context;
};

export const BusinessTrxProvider = ({ children }: PropsWithChildren) => {
  const tableRef = useRef(document.createElement("table"));
  const { state }: { state: IBusinessTrx } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const observerRef = useRef<HTMLTableSectionElement>(null);
  const [meta, setMeta] = useState({
    page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 0,
  });
  const [params, setParams] = useState<Omit<IGetBusinessTrxParams, "page_number">>({
    search: "",
    sort_by: "",
    sort_type: "DESC",
    page_size: 10,
  });
  const [modal, setModal] = useState<ModalType>("");
  const [selectedTrx, setSelectedTrx] = useState<IBusinessTrx | null>(null);
  const [currentStep, setCurrentStep] = useState({ name: stepsConfig[0].name, idx: 0 });
  const { show } = useSnackbar();
  const queryClient = useQueryClient();
  const { userInfo } = useAuth();

  const action = searchParams.get("action");
  const id = searchParams.get("id");
  const isInitialLoad = useRef(true);
  const isEditMode = useMemo(() => action === "edit", [action]);
  const formId = "business-transaction-form";

  const currentTrxFormValues: IBusinessTrxFormValues = useMemo(() => {
    if (!isEditMode || !state) {
      return {
        ...businessTrxDefaultFormValue,
        currentStep: currentStep.idx,
      };
    }

    const {
      trx_desc, sap_bktxt, gnlldr, trx_type, sap_shkzg, trx_code, area_id, pool_id,
      company_code, company_relation, code_segment, department, code_type,
    } = state;

    const poolIds = pool_id?.split(",") || [];
    const companyCodes = company_code?.split(",") || [];
    const companyRelations = company_relation?.split(",").filter(Boolean) || [];
    const segmentCodes = code_segment?.split(",") || [];
    const departments = department?.split(",") || [];

    const selectedCompanyIds = companyCodes.map((code, index) => `${poolIds[index]}.${code}`).filter(Boolean);
    const selectedCompanyRelations = companyRelations.map((relation, index) => `${poolIds[index]}.${relation}`).filter(Boolean);
    const selectedSegmentIds = segmentCodes.map((code, index) => (code ? `${poolIds[index]}.${companyCodes[index]}.${code}` : "")).filter(Boolean);
    const selectedDepartmentIds = departments.map((dep, index) => (dep ? `${poolIds[index]}.${companyCodes[index]}.${segmentCodes[index]}.${dep}` : "")).filter(Boolean);

    return {
      currentStep: currentStep.idx,
      detailTransaksi: {
        deskripsi: trx_desc,
        deskripsiSap: sap_bktxt,
        gl: gnlldr,
        tipeTransaksi: trx_type,
        sap_shkzg,
        trx_code,
        code_type,
      },
      pilihSemuaDepartemen: false,
      pilihSemuaPerusahaan: false,
      pilihSemuaPool: false,
      pilihSemuaSegmen: false,
      selectedAreaIds: area_id?.split(",") || [],
      selectedPoolIds: poolIds,
      selectedCompanyIds,
      selectedCompanyRelations,
      selectedSegmentIds,
      selectedDepartmentIds,
    };
  }, [isEditMode, state]);

  const {
    control, handleSubmit, setValue, reset,
  } = useForm<IBusinessTrxFormValues>({
    resolver: joiResolver(businessTrxSchema),
    mode: "onChange",
    defaultValues: currentTrxFormValues,
  });

  const { mutate: mutateCreate } = useMutation<IResponseMstBusinessTrxData,
    AxiosError<IResponseMstBusinessTrxData>, ICreateMasterBusinessTrxRequest
  >({
    mutationFn: createMasterBusinessTrx,
    onSuccess: (data) => {
      if (data.responseCode === "Failed") show(data.responseMessage, "error");
      else {
        setModal("");
        show("Berhasil menambah data", "success");
        navigate("/master-data/business-transaction", { state: null });
        reset();
      }
    },
    onError: (error) => show(error?.response?.data.responseCode || "Terjadi kesalahan", "error"),
  });

  const { mutate: mutateEdit } = useMutation<IResponseMstBusinessTrxData,
    AxiosError<IResponseMstBusinessTrxData>, IEditMasterBusinessTrxRequest
  >({
    mutationFn: editMasterBusinessTrx,
    onSuccess: (data) => {
      if (data.responseCode === "Failed") show(data.responseMessage, "error");
      else {
        setModal("");
        show("Berhasil menambah data", "success");
        navigate("/master-data/business-transaction", { state: null });
        reset();
      }
    },
    onError: (error) => show(error?.response?.data.responseCode || "Terjadi kesalahan", "error"),
  });

  const { mutate: mutateDelete } = useMutation<IResponseMstBusinessTrxData,
    AxiosError<IResponseMstBusinessTrxData>
  >({
    mutationFn: () => deleteMasterBusinessTrx(String(selectedTrx?.id) || id || ""),
    onSuccess: (data) => {
      if (data.responseCode === "Failed") show(data.responseMessage, "error");
      else {
        setModal("");
        show("Berhasil menghapus data", "success");
        queryClient.invalidateQueries({ queryKey: ["masterBusinessTrxList"] });
        queryClient.removeQueries({ queryKey: ["masterBusinessTrxList"] });
      }
    },
    onError: (error) => show(error?.response?.data.responseCode || "Terjadi kesalahan", "error"),
  });

  const {
    data: rawBusinessTrxList, isFetching, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["masterBusinessTrxList", params],
    queryFn: ({ pageParam = 1 }) => getMasterBusinessTrxList({ ...params, page_number: pageParam })
      .then((res) => {
        if (res.metadata) setMeta(res.metadata);
        return res;
      }),
    keepPreviousData: true,
    getNextPageParam: () => {
      if (!meta) return undefined;
      return meta.page < meta.total_pages ? meta.page + 1 : undefined;
    },
    enabled: !action,
  });

  const businessTrxList = useMemo(() => (rawBusinessTrxList
    ? rawBusinessTrxList.pages.flatMap((page) => page.list_mst_trxcode)
    : []
  ), [rawBusinessTrxList]);

  const {
    data: rawAreaOptions,
  } = useQuery({
    queryKey: ["masterBusinessTrxFilterArea"],
    queryFn: () => getMasterBusinessTrxFilterAllArea(),
    enabled: !action,
  });

  const {
    data: rawPoolOptions,
  } = useQuery({
    queryKey: ["masterBusinessTrxFilterPool", params.area_id],
    queryFn: () => {
      const areaId = params.area_id || "";
      return getMasterBusinessTrxFilterPool(areaId);
    },
    enabled: !action && !!params.area_id,
  });

  const {
    data: rawCompanySegmentOptions,
  } = useQuery({
    queryKey: ["masterBusinessTrxFilterCompanySegment", params.area_id, params.pool_id],
    queryFn: () => {
      const areaId = params.area_id || "";
      const poolId = params.pool_id || "";
      return getMasterBusinessTrxFilterCompanySegment(areaId, poolId);
    },
    enabled: !action && !!params.area_id && !!params.pool_id,
  });

  const {
    data: rawDepartmentOptions,
  } = useQuery({
    queryKey: ["masterBusinessTrxFilterDepartment"],
    queryFn: () => getMasterBusinessTrxFilterDepartment(),
    enabled: !action,
  });

  const areaOptions: IOptionItem[] = useMemo(() => (
    rawAreaOptions ? rawAreaOptions.listareaall.map((area) => ({
      label: area.name,
      value: area.id,
      checked: params.area_id?.includes(area.id),
    })) : []), [params, rawAreaOptions]);

  const poolOptions: IOptionItem[] = useMemo(() => (
    rawPoolOptions ? rawPoolOptions.list_pool.map((pool) => ({
      label: `${pool.pool_id} - ${pool.pool_name}`,
      value: pool.pool_id,
      checked: params.pool_id?.includes(pool.pool_id),
    })) : []), [params, rawPoolOptions]);

  const createUniqueOptions = useCallback(<T, K extends keyof T>(
    items: T[] | undefined,
    key: K,
    checkedValues: string[] | undefined,
  ): IOptionItem[] => {
    if (!items) return [];
    const uniqueItems = [...new Map(items.map((item) => [item[key], item])).values()];
    return uniqueItems.map((item) => {
      const value = item[key] as string;
      return { label: value, value, checked: checkedValues?.includes(value) };
    });
  }, []);

  const companyOptions: IOptionItem[] = useMemo(() => createUniqueOptions<IFilterCompany, "company_code">(
    rawCompanySegmentOptions?.lstmstbusiness,
    "company_code",
    params.company_code ? [params.company_code] : undefined,
  ), [rawCompanySegmentOptions, params.company_code, createUniqueOptions]);

  const segmentOptions: IOptionItem[] = useMemo(() => createUniqueOptions<IFilterCompany, "code_segment">(
    rawCompanySegmentOptions?.lstmstbusiness,
    "code_segment",
    params.code_segment ? [params.code_segment] : undefined,
  ), [rawCompanySegmentOptions, params.code_segment, createUniqueOptions]);

  const departmentOptions: IOptionItem[] = useMemo(() => (
    rawDepartmentOptions ? rawDepartmentOptions.lstmstdepartment.map((dept) => ({
      label: dept.department,
      value: dept.department,
      checked: params.department === dept.department,
    })) : []), [params, rawDepartmentOptions]);

  const filterConfigs = [
    {
      label: "Area",
      paramKey: "area_id",
      options: areaOptions,
      disabled: false,
    },
    {
      label: "Pool",
      paramKey: "pool_id",
      options: poolOptions,
      disabled: !params.area_id,
    },
    {
      label: "Perusahaan",
      paramKey: "company_code",
      options: companyOptions,
      disabled: !params.pool_id || !params.area_id,
    },
    {
      label: "Segmen",
      paramKey: "code_segment",
      options: segmentOptions,
      disabled: !params.pool_id || !params.area_id,
    },
    {
      label: "Departemen",
      paramKey: "department",
      options: departmentOptions,
      disabled: false,
    },
  ];

  const handleFilterSelect = (paramKey: string, selectedOptions: IOptionItem[]) => {
    const newOpt = selectedOptions.map((item) => ({
      ...item,
      checked: params[paramKey as keyof typeof params] === item.value ? false : item.checked,
    }));
    const value = newOpt.find((item) => item.checked);
    setParams((curr) => {
      const newParams = { ...curr, [paramKey]: value?.value as string };
      if (paramKey === "area_id") {
        newParams.pool_id = undefined;
        newParams.company_code = undefined;
        newParams.company_relation = undefined;
        newParams.code_segment = undefined;
      } else if (paramKey === "pool_id") {
        newParams.company_code = undefined;
        newParams.company_relation = undefined;
        newParams.code_segment = undefined;
      } else if (paramKey === "company_code") {
        if (value && rawCompanySegmentOptions) {
          const currComp = rawCompanySegmentOptions.lstmstbusiness
            .find((item) => item.company_code === value.value);
          if (currComp) newParams.company_relation = currComp.company_relation;
          else newParams.company_relation = undefined;
        } else {
          newParams.company_relation = undefined;
        }
      }
      return newParams;
    });
  };

  const columns: ColumnI[] = [
    {
      title: "Tipe & kode transaksi",
      dataIndex: "trx_code",
      key: "trx_code",
      width: "210px",
      render: (item, row) => (
        <>
          <Typography level={7} fontWeight="medium" color={BlueBirdTheme.colors.gray.subtitle}>
            {row.trx_type_name}
          </Typography>
          <br />
          <Typography level={7} color={BlueBirdTheme.colors.gray.bodyText}>{item}</Typography>
        </>
      ),
    },
    {
      title: "Deskripsi",
      dataIndex: "trx_desc",
      key: "trx_desc",
      width: "230px",
    },
    {
      title: "Akun GL",
      dataIndex: "gnlldr",
      key: "gnlldr",
      width: "140px",
    },
    {
      title: "Area",
      dataIndex: "area_id",
      key: "area_id",
      width: "50px",
    },
    {
      title: "Pool",
      dataIndex: "pool_id",
      key: "pool_id",
      width: "50px",
    },
    {
      title: "Perusahaan",
      dataIndex: "company_code",
      key: "company_code",
      width: "100px",
    },
    {
      title: "Departemen",
      dataIndex: "department",
      key: "department",
      width: "140px",
    },
    {
      title: "Segmen",
      dataIndex: "code_segment",
      key: "code_segment",
      width: "60px",
    },
    {
      title: "Debit/kredit",
      dataIndex: "sap_shkzg",
      key: "sap_shkzg",
      width: "140px",
      style: {
        textTransform: "capitalize",
      },
      render: (item) => {
        const statusConfig: {
          content: TrxType;
          status: StatusEnums;
        } = (() => {
          switch (item) {
            case "S":
              return {
                content: "Debit",
                status: StatusEnums.PRIMARY,
              };
            case "H":
              return {
                content: "Kredit",
                status: StatusEnums.WARNING,
              };
            case "X":
              return {
                content: "Semua",
                status: StatusEnums.SUCCESS,
              };
            default:
              return {
                content: "" as TrxType,
                status: StatusEnums.INACTIVE,
              };
          }
        })();
        return (
          <Typography level={7} fontWeight="bold" color={BlueBirdTheme.colors.gray.subtitle}>
            <Status
              content={statusConfig.content}
              status={statusConfig.status}
            />
          </Typography>
        );
      },
    },
    {
      title: "Keterangan",
      dataIndex: "sap_bktxt",
      key: "sap_bktxt",
      width: "140px",
    },
    {
      title: "Action",
      dataIndex: "id",
      key: "action",
      width: "210px",
      render: (_, row) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            style={{ padding: "8px 10px" }}
            backgroundColor={BlueBirdTheme.colors.primary[300]}
            onClick={() => navigate(`?action=edit&id=${row.id}`, {
              state: row,
            })}
          >
            <EditIcon width={16} height={16} color="white" />
          </Button>
          <Button
            style={{ padding: "8px 10px" }}
            backgroundColor={BlueBirdTheme.colors.danger[400]}
            onClick={() => {
              setSelectedTrx(row as IBusinessTrx);
              setModal("DELETE");
            }}
          >
            <TrashIcon width={16} height={16} />
          </Button>
        </div>
      ),
    }];

  const onFormSubmit = (data: typeof businessTrxDefaultFormValue) => {
    if (isEditMode && id) {
      return mutateEdit(formatPayloadEdit(id, data, userInfo.preferred_username));
    }
    return mutateCreate(formatPayload(data, userInfo.preferred_username));
  };

  const onFormError = (formErrors: FieldErrors<IBusinessTrxFormValues>) => {
    if (currentStep.idx === 0) {
      if (formErrors.detailTransaksi) show("Isi detail transaksi terlebih dahulu.", "error");
    } else {
      const steps: (keyof IBusinessTrxFormValues | null)[] = [
        null, "selectedAreaIds", "selectedPoolIds", "selectedCompanyIds", "selectedSegmentIds", "selectedDepartmentIds",
      ];
      const currentFieldName = steps[currentStep.idx];
      if (currentFieldName) {
        const errorObject = formErrors[currentFieldName];
        if (errorObject && errorObject.message) show(errorObject.message, "error");
      }
    }
  };

  const previousStep = () => {
    if (currentStep.idx > 0) {
      setCurrentStep({
        name: stepsConfig[currentStep.idx - 1].name,
        idx: currentStep.idx - 1,
      });
      setValue("currentStep", currentStep.idx - 1, { shouldValidate: true });
    }
  };

  const nextStep = () => {
    if (currentStep.idx < stepsConfig.length - 1) {
      setCurrentStep({
        name: stepsConfig[currentStep.idx + 1].name,
        idx: currentStep.idx + 1,
      });
      setValue("currentStep", currentStep.idx + 1, { shouldValidate: true });
    }
  };

  const onCancelFormClick = () => {
    navigate("/master-data/business-transaction");
    reset();
  };

  const onSubmitFormClick = () => {
    if (currentStep.idx === stepsConfig.length - 1) {
      handleSubmit(() => setModal("CONFIRMATION"), onFormError)();
    } else {
      handleSubmit(nextStep, onFormError)();
    }
  };

  const handleDelete = () => mutateDelete();

  const resetState = () => {
    isInitialLoad.current = true;
    setMeta({
      page: 1, page_size: 10, total_items: 0, total_pages: 0,
    });
    setParams({
      search: "", sort_by: "", sort_type: "DESC", page_size: 10,
    });
    setModal("");
    setSelectedTrx(null);
    setCurrentStep({ name: stepsConfig[0].name, idx: 0 });
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(({ isIntersecting }) => {
      if (isIntersecting && !isFetching) {
        fetchNextPage();
      } else if (meta.page === meta.total_pages && !isFetching) {
        fetchNextPage();
      }
    });
  }, { root: observerRef.current, rootMargin: "0px", threshold: [0] });

  useEffect(() => {
    if (isEditMode && currentTrxFormValues && isInitialLoad.current) {
      Object.keys(currentTrxFormValues).forEach((key) => {
        const field = key as keyof IBusinessTrxFormValues;
        setValue(field, currentTrxFormValues[field], { shouldValidate: true, shouldDirty: true });
      });
      isInitialLoad.current = false;
    }
  }, [isEditMode, currentTrxFormValues, setValue]);

  useEffect(() => {
    if (meta.page === meta.total_pages) observer.unobserve(observerRef.current || document.createElement("tfoot"));
    else observer.observe(observerRef.current || document.createElement("tfoot"));
  }, [meta]);

  useEffect(() => {
    resetState();
    if (!action) reset();
  }, [action]);

  const value = useMemo(() => ({
    params,
    setParams,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    tableRef,
    handleDelete,
    businessTrxList,
    columns,
    action,
    meta,
    setMeta,
    observerRef,
    modal,
    setModal,
    selectedTrx,
    setSelectedTrx,
    isEditMode,
    currentTrxFormValues,
    currentStep,
    setCurrentStep,
    control,
    setValue,
    formId,
    onCancelFormClick,
    onSubmitFormClick,
    previousStep,
    nextStep,
    handleSubmit,
    onFormSubmit,
    onFormError,
    areaOptions,
    poolOptions,
    companyOptions,
    segmentOptions,
    departmentOptions,
    filterConfigs,
    handleFilterSelect,
  }), [params, setParams, hasNextPage, isFetching, isFetchingNextPage, isLoading, tableRef,
    handleDelete, businessTrxList, columns, action, meta, setMeta, observerRef, modal, setModal,
    selectedTrx, setSelectedTrx, isEditMode, currentTrxFormValues, currentStep, setCurrentStep,
    control, setValue, formId, onCancelFormClick, onSubmitFormClick, previousStep, nextStep,
    handleSubmit, onFormSubmit, onFormError, areaOptions, poolOptions, companyOptions,
    departmentOptions, segmentOptions, filterConfigs, handleFilterSelect,
  ]);

  return (
    <Context.Provider value={value}>
      {children}
    </Context.Provider>
  );
};
